import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '../../api/auth'
import { useAdminUsers, useSaveUser, type AdminUser } from '../../api/admin-users'
import {
  ROLE_LABELS, rolePermissionsComplete, roleFromPermissions, validateUserForm, type RoleKey,
} from '@/lib/user-form'
import { DataTable, type Column } from '@/components/DataTable'
import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'

// Marken-Akzente (stahlia.de): Petrol #108474, Amber #FFB300 — nur lokal hier,
// keine globale Theme-Aenderung (Design-Vorgabe User 2026-07-10).
const ROLE_BADGE: Record<RoleKey, string> = {
  admin: 'bg-[#108474]/10 text-[#108474] border-[#108474]/30',
  lagerleiter: 'bg-[#FFB300]/20 text-[#8a6100] border-[#FFB300]/50',
  lager: 'bg-[#FFB300]/10 text-[#8a6100] border-[#FFB300]/30',
  operator: '',
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?'
}

function buildColumns(onEdit: (u: AdminUser) => void): Column<AdminUser>[] {
  return [
    {
      key: 'email',
      header: 'Benutzer',
      cell: (u) => (
        <div className="flex items-center gap-3">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#108474]/10 text-xs font-semibold text-[#108474]">
            {initials(u.name)}
          </div>
          <div>
            <div className="font-medium">{u.name}</div>
            <div className="text-xs text-muted-foreground">{u.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Rolle',
      cell: (u) => {
        const role = roleFromPermissions(u.permissions)
        return <Badge variant="outline" className={ROLE_BADGE[role]}>{ROLE_LABELS[role]}</Badge>
      },
    },
    {
      key: 'is_active',
      header: 'Status',
      cell: (u) =>
        u.is_active ? (
          <Badge className="bg-green-100 text-green-800 border-green-200">Aktiv</Badge>
        ) : (
          <Badge className="bg-gray-100 text-gray-700 border-gray-200">Deaktiviert</Badge>
        ),
    },
    {
      key: 'last_login_at',
      header: 'Letzter Login',
      cell: (u) => (u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('de-DE') : '—'),
    },
    {
      key: 'created_at',
      header: 'Erstellt',
      cell: (u) => new Date(u.created_at).toLocaleDateString('de-DE'),
    },
    {
      key: 'actions',
      header: '',
      cell: (u) => (
        <Button variant="ghost" size="sm" onClick={() => onEdit(u)}>
          Bearbeiten
        </Button>
      ),
    },
  ]
}

export default function Users() {
  const { isAdmin } = useAuth()
  const navigate = useNavigate()
  const { data, isLoading } = useAdminUsers(isAdmin)
  const save = useSaveUser()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<RoleKey>('operator')
  const [password, setPassword] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const { user: me } = useAuth()
  const [editUser, setEditUser] = useState<AdminUser | null>(null)
  const [editName, setEditName] = useState('')
  const [editRole, setEditRole] = useState<RoleKey>('operator')
  const [initialRole, setInitialRole] = useState<RoleKey>('operator')
  const [editPassword, setEditPassword] = useState('')
  const [editPin, setEditPin] = useState('')
  const [clearPin, setClearPin] = useState(false)
  const [editActive, setEditActive] = useState(true)
  const [editError, setEditError] = useState<string | null>(null)

  function openEdit(u: AdminUser) {
    setEditUser(u)
    setEditName(u.name)
    setEditRole(roleFromPermissions(u.permissions))
    setInitialRole(roleFromPermissions(u.permissions))
    setEditPassword('')
    setEditPin('')
    setClearPin(false)
    setEditActive(u.is_active)
    setEditError(null)
  }

  async function handleSaveEdit() {
    if (!editUser) return
    const err = validateUserForm({
      email: editUser.email, name: editName, password: editPassword, passwordRequired: false,
    })
    if (err) { setEditError(err); return }
    if (editPin && !/^\d{6}$/.test(editPin)) {
      setEditError('PIN muss genau 6 Ziffern haben')
      return
    }
    try {
      await save.mutateAsync({
        id: editUser.id,
        body: {
          name: editName.trim(),
          ...(editUser.id !== me?.id && editRole !== initialRole ? { permissions: rolePermissionsComplete(editRole) } : {}),
          is_active: editActive,
          ...(editPassword ? { password: editPassword } : {}),
          ...(clearPin ? { pin: null } : editPin ? { pin: editPin } : {}),
        },
      })
      toast.success(`${editName.trim()} gespeichert`)
      setEditUser(null)
    } catch {
      // Fehler-Toast kommt aus useSaveUser().onError; Dialog bleibt offen
    }
  }

  function resetForm() {
    setEmail(''); setName(''); setRole('operator'); setPassword(''); setFormError(null)
  }

  async function handleCreateUser() {
    const err = validateUserForm({ email, name, password, passwordRequired: true })
    if (err) { setFormError(err); return }
    try {
      await save.mutateAsync({
        body: { email: email.trim(), name: name.trim(), password, permissions: rolePermissionsComplete(role) },
      })
      toast.success(`${name.trim()} angelegt (${ROLE_LABELS[role]})`)
      setDialogOpen(false)
      resetForm()
    } catch {
      // Fehler-Toast kommt aus useSaveUser().onError; Dialog bleibt offen
    }
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive mb-3">Kein Zugriff — nur für Administratoren.</p>
        <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
          ← Zurück
        </Button>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-4xl">
      <PageHeader
        title="Benutzerverwaltung"
        actions={
          <Button size="sm" className="bg-[#108474] hover:bg-[#0d6b5e]" onClick={() => setDialogOpen(true)}>
            Benutzer anlegen
          </Button>
        }
      />

      <DataTable
        columns={buildColumns(openEdit)}
        data={data?.users ?? []}
        rowKey={(u) => u.id}
        isLoading={isLoading}
      />

      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm() }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Benutzer anlegen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Max Mustermann"
              />
            </div>
            <div className="space-y-1.5">
              <Label>E-Mail</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@stahlia.de"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Rolle</Label>
              <Select value={role} onValueChange={(v) => setRole(v as RoleKey)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ROLE_LABELS) as RoleKey[]).map((r) => (
                    <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {role === 'lager' && 'Sieht nur den Lager-Bereich (Einlagern, Umfahren).'}
                {role === 'lagerleiter' && 'Lager komplett inkl. Wareneingang, Klärfälle, Mitarbeiter-PINs.'}
                {role === 'operator' && 'Aufträge, Artikel und Berichte — kein Admin, kein Lager.'}
                {role === 'admin' && 'Voller Zugriff auf alle Bereiche inkl. Benutzerverwaltung.'}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Passwort</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="mind. 12 Zeichen"
              />
            </div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              className="bg-[#108474] hover:bg-[#0d6b5e]"
              onClick={() => void handleCreateUser()}
              disabled={!email || !password || !name || save.isPending}
            >
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editUser !== null} onOpenChange={(o) => { if (!o) setEditUser(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Benutzer bearbeiten</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">{editUser.email}</p>
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Rolle</Label>
                <Select value={editRole} onValueChange={(v) => setEditRole(v as RoleKey)} disabled={editUser.id === me?.id}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(ROLE_LABELS) as RoleKey[]).map((r) => (
                      <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editUser.id === me?.id && (
                  <p className="text-xs text-muted-foreground">Eigene Rolle kann nicht geändert werden.</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Neues Passwort (leer = unverändert)</Label>
                <Input
                  type="password"
                  value={editPassword}
                  onChange={(e) => setEditPassword(e.target.value)}
                  placeholder="mind. 12 Zeichen"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Freigabe-PIN (6 Ziffern, leer = unverändert)</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={editPin}
                  disabled={clearPin}
                  onChange={(e) => setEditPin(e.target.value)}
                  placeholder="6-stellige Zahl"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={clearPin}
                  onChange={(e) => {
                    setClearPin(e.target.checked)
                    if (e.target.checked) setEditPin('')
                  }}
                />
                PIN löschen
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editActive}
                  disabled={editUser.id === me?.id}
                  onChange={(e) => setEditActive(e.target.checked)}
                />
                Aktiv
                {editUser.id === me?.id && (
                  <span className="text-xs text-muted-foreground">(eigener Account — nicht deaktivierbar)</span>
                )}
              </label>
              {editError && <p className="text-sm text-destructive">{editError}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Abbrechen</Button>
            <Button
              className="bg-[#108474] hover:bg-[#0d6b5e]"
              onClick={() => void handleSaveEdit()}
              disabled={save.isPending}
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
